import React, { useState, useEffect } from 'react'
import {
  Package,
  Search,
  Filter,
  Eye,
  Edit,
  CheckCircle,
  Clock,
  AlertCircle,
  QrCode,
  User,
  MapPin,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  X,
  XCircle,
  RefreshCw
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { formatPrice } from '../../lib/square'
import OrderStatusIndicator from '../orders/OrderStatusIndicator'
import LoadingSpinner from '../ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface OrderWithDetails {
  id: string
  order_number: string
  total_amount: number
  status: string
  created_at: string
  pickup_time?: string
  square_payment_id?: string  // Task 5: Add payment_id for validation
  secret_users: {
    first_name: string
    last_name: string
    email?: string
    phone?: string
  }
  secret_events: {
    title: string
    location_name: string
    event_date: string
  }
  secret_order_items: Array<{
    quantity: number
    unit_price: number
    secret_menu_items: {
      name: string
    }
  }>
}

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<OrderWithDetails[]>([])
  const [filteredOrders, setFilteredOrders] = useState<OrderWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [eventFilter, setEventFilter] = useState<string>('all')
  const [events, setEvents] = useState<any[]>([])

  // Phase 4: Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [ordersPerPage] = useState(10)

  // Phase 4: Bulk actions
  const [selectedOrders, setSelectedOrders] = useState<string[]>([])
  const [bulkActionStatus, setBulkActionStatus] = useState<string>('')

  // Phase 4: Order details modal
  const [selectedOrder, setSelectedOrder] = useState<OrderWithDetails | null>(null)
  const [showOrderModal, setShowOrderModal] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    filterOrders()
  }, [orders, searchTerm, statusFilter, eventFilter])

  const loadData = async () => {
    try {
      setLoading(true)

      // Load orders with user and event details
      // Note: Using secret_users!user_id to explicitly specify the user_id FK relationship
      // (secret_orders has 2 FKs to secret_users: user_id and transferred_to)
      const { data: ordersData, error: ordersError } = await supabase
        .from('secret_orders')
        .select(`
          *,
          secret_users!user_id (
            first_name,
            last_name,
            email,
            phone
          ),
          secret_events (
            title,
            location_name,
            event_date
          ),
          secret_order_items (
            quantity,
            unit_price,
            secret_menu_items (
              name
            )
          )
        `)
        .order('created_at', { ascending: false })

      if (ordersError) {
        console.error('Orders query error:', ordersError)
        throw ordersError
      }

      // Load events for filter dropdown
      const { data: eventsData, error: eventsError } = await supabase
        .from('secret_events')
        .select('id, title, event_date, status')
        .order('event_date', { ascending: false })

      if (eventsError) {
        console.error('Events query error:', eventsError)
        throw eventsError
      }

      setOrders(ordersData || [])
      setEvents(eventsData || [])

    } catch (error: any) {
      console.error('Failed to load orders:', error)
      // Only show error toast for real database/network errors
      // Don't show toast for empty results or permission issues that are expected
      const isRealError = error && error.message &&
        !error.message.includes('no rows') &&
        !error.code?.includes('PGRST116') // Postgrest "not found" code

      if (isRealError) {
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            title="Loading Failed"
            message={error.message || 'Could not load orders'}
            type="error"
          />
        ))
      }
    } finally {
      setLoading(false)
    }
  }

  const filterOrders = () => {
    let filtered = orders

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(order => 
        order.order_number.toLowerCase().includes(search) ||
        order.secret_users.first_name.toLowerCase().includes(search) ||
        order.secret_users.last_name.toLowerCase().includes(search) ||
        order.secret_users.email?.toLowerCase().includes(search) ||
        order.secret_events.title.toLowerCase().includes(search)
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter)
    }

    // Event filter
    if (eventFilter !== 'all') {
      filtered = filtered.filter(order => order.event_id === eventFilter)
    }

    setFilteredOrders(filtered)
  }

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      // Task 5: Prevent confirming unpaid orders
      if (newStatus === 'confirmed') {
        // Check if order has payment_id
        const order = orders.find(o => o.id === orderId)
        if (order && !order.square_payment_id) {
          // Show confirmation dialog for unpaid orders
          const confirmed = window.confirm(
            '⚠️ WARNING: This order has no associated payment.\n\n' +
            'Marking it as "confirmed" without payment may result in:\n' +
            '• Revenue discrepancies\n' +
            '• Unpaid orders being fulfilled\n' +
            '• Accounting issues\n\n' +
            'Are you sure you want to confirm this unpaid order?'
          )

          if (!confirmed) {
            return // User cancelled
          }
        }
      }

      const { error } = await supabase
        .from('secret_orders')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId)

      if (error) throw error

      // Update local state
      setOrders(prev => prev.map(order =>
        order.id === orderId
          ? { ...order, status: newStatus }
          : order
      ))

      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="Status Updated"
          message={`Order status changed to ${newStatus}`}
          type="success"
        />
      ))
    } catch (error) {
      console.error('Failed to update order status:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="Update Failed"
          message="Could not update order status"
          type="error"
        />
      ))
    }
  }


  const getStatusOptions = (currentStatus: string) => {
    const allStatuses = ['pending', 'confirmed', 'preparing', 'ready', 'completed', 'cancelled']
    return allStatuses.filter(status => status !== currentStatus)
  }

  // Phase 4: Pagination logic
  const indexOfLastOrder = currentPage * ordersPerPage
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder)
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage)

  // Phase 4: Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedOrders.length === currentOrders.length) {
      setSelectedOrders([])
    } else {
      setSelectedOrders(currentOrders.map(order => order.id))
    }
  }

  const toggleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    )
  }

  // Phase 4: Bulk actions handler
  const handleBulkAction = async () => {
    if (!bulkActionStatus || selectedOrders.length === 0) return

    const confirmed = window.confirm(
      `Are you sure you want to change ${selectedOrders.length} order(s) to "${bulkActionStatus}"?`
    )

    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('secret_orders')
        .update({
          status: bulkActionStatus,
          updated_at: new Date().toISOString()
        })
        .in('id', selectedOrders)

      if (error) throw error

      // Update local state
      setOrders(prev => prev.map(order =>
        selectedOrders.includes(order.id)
          ? { ...order, status: bulkActionStatus }
          : order
      ))

      setSelectedOrders([])
      setBulkActionStatus('')

      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="Bulk Update Complete"
          message={`${selectedOrders.length} order(s) updated to ${bulkActionStatus}`}
          type="success"
        />
      ))
    } catch (error) {
      console.error('Failed to update orders:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="Bulk Update Failed"
          message="Could not update selected orders"
          type="error"
        />
      ))
    }
  }

  // Phase 4: Quick action handlers
  const quickCancelOrder = async (orderId: string) => {
    const confirmed = window.confirm(
      '⚠️ Are you sure you want to cancel this order?\n\nThis action will:\n• Mark the order as cancelled\n• Update the order timestamp\n\nThis action cannot be easily undone.'
    )

    if (!confirmed) return
    await updateOrderStatus(orderId, 'cancelled')
  }

  const quickCompleteOrder = async (orderId: string) => {
    await updateOrderStatus(orderId, 'completed')
  }

  // Phase 4: Order details modal handlers
  const openOrderModal = (order: OrderWithDetails) => {
    setSelectedOrder(order)
    setShowOrderModal(true)
  }

  const closeOrderModal = () => {
    setSelectedOrder(null)
    setShowOrderModal(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground flex items-center space-x-3">
            <Package size={32} className="text-green-600" />
            <span>Order Management</span>
          </h2>
          <p className="text-muted-foreground mt-2">
            Manage and track all SECRET orders
          </p>
        </div>

      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="secret-input pl-11 h-11 border-border focus:border-secret-primary focus:ring-2 focus:ring-secret-primary/20 transition-all"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="secret-select h-11 border-border focus:border-secret-primary focus:ring-2 focus:ring-secret-primary/20 transition-all"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Event Filter */}
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="secret-select h-11 border-border focus:border-secret-primary focus:ring-2 focus:ring-secret-primary/20 transition-all"
          >
            <option value="all">All Events</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.title}
              </option>
            ))}
          </select>

          {/* Results Count */}
          <div className="flex items-center justify-center bg-muted/30 rounded-lg px-4 py-2 border border-border">
            <Filter size={18} className="mr-2 text-secret-primary" />
            <span className="font-semibold text-foreground">{filteredOrders.length}</span>
            <span className="text-muted-foreground ml-1">orders</span>
          </div>
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedOrders.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-foreground font-semibold">
                {selectedOrders.length} order{selectedOrders.length !== 1 ? 's' : ''} selected
              </span>
              <select
                value={bulkActionStatus}
                onChange={(e) => setBulkActionStatus(e.target.value)}
                className="secret-select h-10 border-border"
              >
                <option value="">Select status...</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="preparing">Preparing</option>
                <option value="ready">Ready</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <button
                onClick={handleBulkAction}
                disabled={!bulkActionStatus}
                className="secret-btn-primary h-10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Selected
              </button>
            </div>
            <button
              onClick={() => setSelectedOrders([])}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left p-4 w-12">
                  <input
                    type="checkbox"
                    checked={selectedOrders.length === currentOrders.length && currentOrders.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-secret-primary rounded border-border focus:ring-2 focus:ring-secret-primary/20"
                  />
                </th>
                <th className="text-left p-4 text-foreground font-semibold">Order</th>
                <th className="text-left p-4 text-foreground font-semibold">Customer</th>
                <th className="text-left p-4 text-foreground font-semibold">Event</th>
                <th className="text-left p-4 text-foreground font-semibold">Status</th>
                <th className="text-left p-4 text-foreground font-semibold">Total</th>
                <th className="text-left p-4 text-foreground font-semibold">Created</th>
                <th className="text-left p-4 text-foreground font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order, index) => (
                <tr key={order.id} className={`border-t border-border hover:bg-muted/30 transition-colors ${index % 2 === 0 ? 'bg-card' : 'bg-muted/5'}`}>
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(order.id)}
                      onChange={() => toggleSelectOrder(order.id)}
                      className="w-4 h-4 text-secret-primary rounded border-border focus:ring-2 focus:ring-secret-primary/20"
                    />
                  </td>
                  <td className="p-4">
                    <div>
                      <div className="font-medium text-foreground">#{order.order_number}</div>
                      <div className="text-secret-text-secondary text-sm">
                        {order.secret_order_items.length} items
                      </div>
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <div>
                      <div className="text-foreground font-medium">
                        {order.secret_users.first_name} {order.secret_users.last_name}
                      </div>
                      <div className="text-secret-text-secondary text-sm">
                        {order.secret_users.email || order.secret_users.phone}
                      </div>
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <div>
                      <div className="text-foreground font-medium">{order.secret_events.title}</div>
                      <div className="text-secret-text-secondary text-sm flex items-center space-x-1">
                        <MapPin size={12} />
                        <span>{order.secret_events.location_name}</span>
                      </div>
                      <div className="text-secret-text-secondary text-xs">
                        {new Date(order.secret_events.event_date).toLocaleDateString()}
                      </div>
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <OrderStatusIndicator
                      status={order.status}
                      showLabel={false}
                      size="sm"
                    />
                    
                    {/* Status Change Dropdown */}
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      className="secret-select text-xs mt-2 py-1 px-2"
                    >
                      <option value={order.status}>{order.status}</option>
                      {getStatusOptions(order.status).map(status => (
                        <option key={status} value={status}>
                          Change to {status}
                        </option>
                      ))}
                    </select>
                  </td>
                  
                  <td className="p-4">
                    <div className="text-secret-primary font-bold">
                      {formatPrice(order.total_amount * 100)}
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <div className="text-foreground text-sm">
                      {new Date(order.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-secret-text-secondary text-xs">
                      {new Date(order.created_at).toLocaleTimeString()}
                    </div>
                  </td>
                  
                  <td className="p-4">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => openOrderModal(order)}
                        className="text-secret-primary hover:text-secret-primary-dark hover:bg-secret-primary/10 p-2 rounded-md transition-all"
                        title="View Details"
                      >
                        <Eye size={18} />
                      </button>

                      {order.status !== 'completed' && order.status !== 'cancelled' && (
                        <button
                          onClick={() => quickCompleteOrder(order.id)}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 p-2 rounded-md transition-all"
                          title="Mark as Completed"
                        >
                          <CheckCircle size={18} />
                        </button>
                      )}

                      {order.status !== 'cancelled' && (
                        <button
                          onClick={() => quickCancelOrder(order.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 p-2 rounded-md transition-all"
                          title="Cancel Order"
                        >
                          <XCircle size={18} />
                        </button>
                      )}

                      {['ready', 'completed'].includes(order.status) && (
                        <button
                          onClick={() => window.open(`/verify?o=${order.id}`, '_blank')}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 p-2 rounded-md transition-all"
                          title="View QR Code"
                        >
                          <QrCode size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div className="text-center py-16">
            <Package size={48} className="text-secret-text-tertiary mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">No Orders Found</h3>
            <p className="text-secret-text-secondary">
              {searchTerm || statusFilter !== 'all' || eventFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No orders have been placed yet'
              }
            </p>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="border-t border-border p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, filteredOrders.length)} of {filteredOrders.length} orders
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded-md transition-colors ${
                        currentPage === page
                          ? 'bg-secret-primary text-white'
                          : 'border border-border hover:bg-muted text-foreground'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-md border border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats - Modern Card Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <Package className="text-secret-primary" size={24} />
            <span className="text-3xl font-bold text-foreground">{filteredOrders.length}</span>
          </div>
          <h3 className="font-semibold text-foreground mb-1">Total Orders</h3>
          <p className="text-muted-foreground text-sm">
            {orders.length > filteredOrders.length
              ? `${filteredOrders.length} of ${orders.length} shown`
              : 'All time'}
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <Clock className="text-blue-600" size={24} />
            <span className="text-3xl font-bold text-foreground">
              {filteredOrders.filter(o => ['confirmed', 'preparing', 'ready'].includes(o.status)).length}
            </span>
          </div>
          <h3 className="font-semibold text-foreground mb-1">Active Orders</h3>
          <p className="text-muted-foreground text-sm">Currently processing</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle className="text-green-600" size={24} />
            <span className="text-3xl font-bold text-foreground">
              {filteredOrders.filter(o => o.status === 'completed').length}
            </span>
          </div>
          <h3 className="font-semibold text-foreground mb-1">Completed</h3>
          <p className="text-muted-foreground text-sm">Successfully fulfilled</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="text-secret-primary" size={24} />
            <span className="text-3xl font-bold text-secret-primary">
              {formatPrice(filteredOrders
                .filter(o => o.status !== 'cancelled')
                .reduce((sum, o) => sum + o.total_amount, 0) * 100
              )}
            </span>
          </div>
          <h3 className="font-semibold text-foreground mb-1">Total Revenue</h3>
          <p className="text-muted-foreground text-sm">Excluding cancelled</p>
        </div>
      </div>

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-foreground">Order Details</h3>
                <p className="text-muted-foreground text-sm">#{selectedOrder.order_number}</p>
              </div>
              <button
                onClick={closeOrderModal}
                className="text-muted-foreground hover:text-foreground p-2 rounded-md hover:bg-muted transition-all"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Order Status */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Status</h4>
                <OrderStatusIndicator status={selectedOrder.status} showLabel={true} size="md" />
              </div>

              {/* Customer Info */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Customer</h4>
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-foreground font-medium">
                    {selectedOrder.secret_users.first_name} {selectedOrder.secret_users.last_name}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {selectedOrder.secret_users.email || selectedOrder.secret_users.phone}
                  </p>
                </div>
              </div>

              {/* Event Info */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Event</h4>
                <div className="bg-muted/30 rounded-lg p-4">
                  <p className="text-foreground font-medium">{selectedOrder.secret_events.title}</p>
                  <p className="text-muted-foreground text-sm flex items-center space-x-1 mt-1">
                    <MapPin size={14} />
                    <span>{selectedOrder.secret_events.location_name}</span>
                  </p>
                  <p className="text-muted-foreground text-sm mt-1">
                    {new Date(selectedOrder.secret_events.event_date).toLocaleDateString()} at {new Date(selectedOrder.secret_events.event_date).toLocaleTimeString()}
                  </p>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Order Items</h4>
                <div className="space-y-2">
                  {selectedOrder.secret_order_items.map((item, idx) => (
                    <div key={idx} className="bg-muted/30 rounded-lg p-4 flex justify-between items-center">
                      <div>
                        <p className="text-foreground font-medium">{item.secret_menu_items.name}</p>
                        <p className="text-muted-foreground text-sm">Quantity: {item.quantity}</p>
                      </div>
                      <p className="text-foreground font-bold">{formatPrice(item.unit_price * 100)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Total */}
              <div className="border-t border-border pt-4">
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-semibold text-foreground">Total</h4>
                  <p className="text-2xl font-bold text-secret-primary">
                    {formatPrice(selectedOrder.total_amount * 100)}
                  </p>
                </div>
              </div>

              {/* Payment Status */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Payment</h4>
                <div className={`rounded-lg p-4 ${selectedOrder.square_payment_id ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                  <p className={`font-medium ${selectedOrder.square_payment_id ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                    {selectedOrder.square_payment_id ? '✓ Payment Received' : '⚠ No Payment Record'}
                  </p>
                  {selectedOrder.square_payment_id && (
                    <p className="text-muted-foreground text-sm mt-1">ID: {selectedOrder.square_payment_id}</p>
                  )}
                </div>
              </div>

              {/* Timestamps */}
              <div>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">Timeline</h4>
                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="text-foreground">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                  </div>
                  {selectedOrder.pickup_time && (
                    <div>
                      <p className="text-xs text-muted-foreground">Pickup Time</p>
                      <p className="text-foreground">{new Date(selectedOrder.pickup_time).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-card border-t border-border p-6 flex justify-end space-x-3">
              <button
                onClick={closeOrderModal}
                className="secret-btn-secondary"
              >
                Close
              </button>
              <button
                onClick={() => window.open(`/orders/${selectedOrder.id}`, '_blank')}
                className="secret-btn-primary"
              >
                View Full Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderManagement